import {APP_INITIALIZER, ErrorHandler, Injectable, Injector, Provider} from '@angular/core';
import {NavigationError, Router} from '@angular/router';

const RELOAD_KEY = 'chunkReloadAt';
const RELOAD_WINDOW_MS = 10000;

// A lazy chunk fails to load when the tab was opened before a deploy that replaced it.
export function isChunkLoadError(error: any): boolean {
    const err = error && error.rejection ? error.rejection : error;
    if (!err) {
        return false;
    }
    return err.name === 'ChunkLoadError' || /Loading chunk [\w-]+ failed/.test(String(err.message || err));
}

// Allows one reload per window, so a chunk that is really missing can't cause a reload loop.
export function claimChunkReload(now = Date.now()): boolean {
    try {
        const last = Number(sessionStorage.getItem(RELOAD_KEY));
        if (last && now - last < RELOAD_WINDOW_MS) {
            return false;
        }
        sessionStorage.setItem(RELOAD_KEY, String(now));
        return true;
    } catch (e) {
        return false;
    }
}

@Injectable()
export class ChunkReloadErrorHandler extends ErrorHandler {
    handleError(error: any): void {
        if (isChunkLoadError(error) && claimChunkReload()) {
            window.location.reload();
            return;
        }
        super.handleError(error);
    }
}

// Reloads straight into the page the user clicked, instead of silently staying where they were.
export function reloadOnChunkNavigationError(injector: Injector): () => void {
    return () => {
        injector.get(Router).events.subscribe(event => {
            if (event instanceof NavigationError && isChunkLoadError(event.error) && claimChunkReload()) {
                window.location.assign(event.url);
            }
        });
    };
}

export const CHUNK_RELOAD_PROVIDERS: Provider[] = [
    {provide: ErrorHandler, useClass: ChunkReloadErrorHandler},
    {provide: APP_INITIALIZER, useFactory: reloadOnChunkNavigationError, deps: [Injector], multi: true}
];
